/**
 * AdminMauBillingCard — Section B1 (Super Admin Dashboard) for the Obondo
 * Metered Billing Engine. All data is live against `/admin/mau/*`
 * (Section A backend, Part 1). No mocked data — every widget renders
 * whatever is actually in `mau_clients`/`mau_records` right now.
 *
 * Includes a "New test client" creator since there is no real onboarding
 * flow yet (that lands with the Part 3 payment gateway) — this keeps the
 * dashboard demoable with live data per the build's success criteria.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    PieChart, Pie, Cell, Tooltip as RTooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Radio, PauseCircle, WifiOff, KeyRound, Ban, Wallet, Plus } from "lucide-react";

const PIE_COLORS = ["#F5A623", "#4C9AFF", "#36B37E"];

const TOKEN_STATE_STYLE = {
    normal:    { label: "Normal",    className: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" },
    alert:     { label: "Alert (≤10%)",   className: "bg-amber-500/15 text-amber-500 border-amber-500/30" },
    throttle:  { label: "Throttled (≤2%)", className: "bg-orange-500/15 text-orange-500 border-orange-500/30" },
    suspended: { label: "Suspended (0%)", className: "bg-red-500/15 text-red-500 border-red-500/30" },
};

function usd(n) {
    if (n == null) return "—";
    return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function AdminMauBillingCard() {
    const [clients, setClients] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [snapshot, setSnapshot] = useState(null);
    const [traders, setTraders] = useState([]);
    const [overlap, setOverlap] = useState([]);
    const [platform, setPlatform] = useState(null);
    const [triggers, setTriggers] = useState([]);
    const [flags, setFlags] = useState([]);
    const [busy, setBusy] = useState(false);
    const [newClient, setNewClient] = useState({ owner_type: "individual", owner_id: "", label: "" });
    const [newTraderRef, setNewTraderRef] = useState("");

    const loadAll = useCallback(async (keepSelected) => {
        const [{ data: cl }, { data: ov }, { data: pl }, { data: tr }, { data: fl }] = await Promise.all([
            api.get("/admin/mau/clients"),
            api.get("/admin/mau/fair-use-overlap"),
            api.get("/admin/mau/platform-summary"),
            api.get("/admin/mau/trigger-monitor"),
            api.get("/admin/mau/feature-flags"),
        ]);
        setClients(cl.clients || []);
        setOverlap(ov.clients || []);
        setPlatform(pl);
        setTriggers(tr.flagged || []);
        setFlags(fl.flags || []);
        const stillExists = keepSelected && (cl.clients || []).some((c) => c.client_id === keepSelected);
        const nextId = stillExists ? keepSelected : (cl.clients?.[0]?.client_id || null);
        setSelectedId(nextId);
    }, []);

    useEffect(() => { loadAll(null); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const loadClientDetail = useCallback(async (id) => {
        if (!id) { setSnapshot(null); setTraders([]); return; }
        const [{ data: snap }, { data: tr }] = await Promise.all([
            api.get(`/admin/mau/clients/${id}`),
            api.get(`/admin/mau/clients/${id}/traders`),
        ]);
        setSnapshot(snap);
        setTraders(tr.traders || []);
    }, []);

    useEffect(() => { loadClientDetail(selectedId); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selectedId]);

    async function createClient() {
        if (!newClient.owner_id.trim()) return toast.error("owner_id is required");
        setBusy(true);
        try {
            const { data } = await api.post("/admin/mau/clients", newClient);
            toast.success(`Client "${data.label}" created (Standard tier, 7-day trial)`);
            setNewClient({ owner_type: "individual", owner_id: "", label: "" });
            await loadAll(data.id);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to create client"); }
        finally { setBusy(false); }
    }

    async function fireTestEvent(eventType) {
        if (!selectedId) return;
        const ref = newTraderRef.trim() || `trader_${Math.random().toString(36).slice(2, 8)}`;
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/mau/clients/${selectedId}/events`, { trader_ref: ref, event_type: eventType });
            toast.success(data.newly_billed ? `${ref} → newly Billed MAU (${data.total_billed_mau_this_cycle} total)` : `${ref} already billed this cycle`);
            setNewTraderRef(ref);
            await Promise.all([loadClientDetail(selectedId), loadAll(selectedId)]);
        } catch (e) { toast.error(e.response?.data?.detail || "Event failed"); }
        finally { setBusy(false); }
    }

    async function simulateRequest(n = 1) {
        if (!selectedId) return;
        const ref = newTraderRef.trim() || "trader_demo";
        setBusy(true);
        try {
            let last;
            for (let i = 0; i < n; i++) {
                // eslint-disable-next-line no-await-in-loop
                last = (await api.post(`/admin/mau/clients/${selectedId}/simulate-request`, { trader_ref: ref })).data;
            }
            toast[last.allowed ? "success" : "error"](`HTTP ${last.http_status} — ${last.reason || (last.throttled ? "throttled" : "allowed")}`);
            await Promise.all([loadClientDetail(selectedId), loadAll(selectedId)]);
        } catch (e) { toast.error(e.response?.data?.detail || "Simulate failed"); }
        finally { setBusy(false); }
    }

    async function toggleKillSwitch(action, on) {
        if (!selectedId) return;
        setBusy(true);
        try {
            await api.post(`/admin/mau/clients/${selectedId}/kill-switch/${action}`, { on });
            toast.success(`${action.replace(/-/g, " ")} → ${on ? "ON" : "OFF"}`);
            await loadClientDetail(selectedId);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    }

    async function forceTopUp() {
        if (!selectedId) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/mau/clients/${selectedId}/force-topup`, { amount_usd: 200 });
            toast.success(`Buffer topped up → ${usd(data.cash_buffer_usd)} (${data.token_state})`);
            setSnapshot(data);
            await loadAll(selectedId);
        } catch (e) { toast.error(e.response?.data?.detail || "Top-up failed"); }
        finally { setBusy(false); }
    }

    async function toggleFlag(key, enabled) {
        setBusy(true);
        try {
            await api.post(`/admin/mau/feature-flags/${key}/toggle`, { enabled });
            const { data } = await api.get("/admin/mau/feature-flags");
            setFlags(data.flags || []);
        } catch (e) { toast.error("Toggle failed"); }
        finally { setBusy(false); }
    }

    const pieData = platform ? [
        { name: "Standard", value: platform.tier_distribution.standard },
        { name: "Growth", value: platform.tier_distribution.growth },
        { name: "Scale", value: platform.tier_distribution.scale },
    ] : [];

    const tokenStyle = snapshot ? TOKEN_STATE_STYLE[snapshot.token_state] : null;

    return (
        <div className="space-y-6" data-testid="admin-mau-billing-card">
            {/* Client selector + creator */}
            <div className="border border-border bg-card rounded p-4 flex flex-wrap items-end gap-3">
                <div className="min-w-[220px]">
                    <Label className="text-xs text-muted-foreground">API client</Label>
                    <Select value={selectedId || ""} onValueChange={setSelectedId}>
                        <SelectTrigger data-testid="mau-client-select"><SelectValue placeholder="Select a client" /></SelectTrigger>
                        <SelectContent>
                            {clients.map((c) => (
                                <SelectItem key={c.client_id} value={c.client_id}>{c.label} · {c.tier_label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex items-end gap-2">
                    <Select value={newClient.owner_type} onValueChange={(v) => setNewClient((s) => ({ ...s, owner_type: v }))}>
                        <SelectTrigger className="w-[130px]" data-testid="mau-new-owner-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="individual">Individual</SelectItem>
                            <SelectItem value="agency">Agency</SelectItem>
                        </SelectContent>
                    </Select>
                    <Input placeholder="owner_id" value={newClient.owner_id} data-testid="mau-new-owner-id"
                        onChange={(e) => setNewClient((s) => ({ ...s, owner_id: e.target.value }))} className="w-[140px]" />
                    <Input placeholder="Label" value={newClient.label} data-testid="mau-new-label"
                        onChange={(e) => setNewClient((s) => ({ ...s, label: e.target.value }))} className="w-[140px]" />
                    <Button size="sm" onClick={createClient} disabled={busy} data-testid="mau-create-client-btn">
                        <Plus className="w-3.5 h-3.5 mr-1" /> New test client
                    </Button>
                </div>
            </div>

            {!selectedId && (
                <div className="text-sm text-muted-foreground border border-dashed border-border rounded p-6 text-center">
                    No API clients yet — create one above to see live MAU/billing data.
                </div>
            )}

            {selectedId && snapshot && (
                <>
                    {/* Token enforcement banner */}
                    {snapshot.token_state !== "normal" && (
                        <div className={`border rounded p-3 flex items-center gap-2 text-sm font-semibold ${tokenStyle.className}`} data-testid="mau-token-banner">
                            <AlertTriangle className="w-4 h-4 shrink-0" />
                            Cash buffer at {snapshot.cash_buffer_pct_remaining}% — {tokenStyle.label}.
                            {snapshot.token_state === "throttle" && " Real-time trade pushes are paused; dashboards remain active."}
                            {snapshot.token_state === "suspended" && " New API payloads are rejected with HTTP 402."}
                            {" Reload funds to restore normal service."}
                        </div>
                    )}

                    {/* Top stat row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <StatBox label="Billed MAUs this cycle" value={snapshot.total_billed_mau_this_cycle} sub={snapshot.tier_label} testId="mau-stat-mau" />
                        <StatBox label="Estimated invoice" value={usd(snapshot.estimated_invoice_usd)} sub={snapshot.trial_active ? "Trial — $0 billed" : `@ $${snapshot.price_per_user_usd}/MAU`} testId="mau-stat-invoice" />
                        <StatBox label="Cash buffer" value={usd(snapshot.cash_buffer_usd)} sub={`${snapshot.cash_buffer_pct_remaining}% of last top-up`} testId="mau-stat-buffer" />
                        <StatBox label="Cycle resets in" value={`${snapshot.days_remaining_in_cycle}d ${snapshot.hours_remaining_in_cycle}h`} sub={`Slots: ${snapshot.total_billed_mau_this_cycle}/${snapshot.prepaid_slots} (${snapshot.slots_status.replace(/_/g, " ")})`} testId="mau-stat-cycle" />
                    </div>

                    {/* Test-event triggers */}
                    <div className="border border-border bg-card rounded p-4">
                        <div className="overline mb-2">Simulate live activity (Section A engine)</div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Input placeholder="trader_ref (auto if blank)" value={newTraderRef} onChange={(e) => setNewTraderRef(e.target.value)} className="w-[180px]" data-testid="mau-trader-ref-input" />
                            {["behavioral_nudge", "profile_assessment", "trade_executed", "price_push", "behavioral_trade_idea", "dashboard_open"].map((et) => (
                                <Button key={et} size="sm" variant="outline" disabled={busy} onClick={() => fireTestEvent(et)} data-testid={`mau-fire-event-${et}`}>{et.replace(/_/g, " ")}</Button>
                            ))}
                            <Button size="sm" variant="secondary" disabled={busy} onClick={() => simulateRequest(1)} data-testid="mau-simulate-request-btn">Simulate 1 API request</Button>
                            <Button size="sm" variant="secondary" disabled={busy} onClick={() => simulateRequest(60)} data-testid="mau-simulate-burst-btn">Simulate 60/sec burst</Button>
                        </div>
                    </div>

                    {/* Kill switches */}
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="border border-border bg-card rounded p-4">
                            <div className="overline mb-3">Global kill switch — Prepaid Slots</div>
                            <div className="space-y-2">
                                <KillSwitchRow icon={PauseCircle} label="Pause Trade Ideas" active={snapshot.kill_switch.trade_ideas_paused}
                                    onToggle={(v) => toggleKillSwitch("pause-trade-ideas", v)} testId="mau-kill-pause-trade-ideas" />
                                <KillSwitchRow icon={WifiOff} label="Restrict Data Feeds" active={snapshot.kill_switch.data_feed_restricted}
                                    onToggle={(v) => toggleKillSwitch("restrict-data-feeds", v)} testId="mau-kill-restrict-feeds" />
                                <KillSwitchRow icon={KeyRound} label="Revoke Key Safely" active={snapshot.kill_switch.key_revoked}
                                    onToggle={(v) => toggleKillSwitch("revoke-key", v)} testId="mau-kill-revoke-key" />
                            </div>
                        </div>
                        <div className="border border-border bg-card rounded p-4">
                            <div className="overline mb-3">Global kill switch — Cash Buffer</div>
                            <div className="space-y-2">
                                <KillSwitchRow icon={Ban} label="Cut Off Overage Users" active={snapshot.kill_switch.overage_users_cutoff}
                                    onToggle={(v) => toggleKillSwitch("cutoff-overage-users", v)} testId="mau-kill-cutoff-overage" />
                                <div className="flex items-center justify-between border border-border rounded px-3 py-2">
                                    <div className="flex items-center gap-2 text-sm"><Wallet className="w-4 h-4 text-muted-foreground" /> Force Buffer Top-Up (+$200)</div>
                                    <Button size="sm" onClick={forceTopUp} disabled={busy} data-testid="mau-force-topup-btn">Charge card on file</Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Unique trader log */}
                    <div className="border border-border bg-card rounded p-4">
                        <div className="overline mb-2">Unique Trader ID Logs — this cycle</div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs" data-testid="mau-traders-table">
                                <thead><tr className="text-left text-muted-foreground border-b border-border">
                                    <th className="py-1.5 pr-3">Trader ref</th><th className="pr-3">Billed</th>
                                    <th className="pr-3">First event</th><th className="pr-3">Requests</th>
                                    <th className="pr-3">Overage cost</th><th>Status</th>
                                </tr></thead>
                                <tbody>
                                    {traders.length === 0 && <tr><td colSpan={6} className="py-3 text-center text-muted-foreground">No trader activity this cycle.</td></tr>}
                                    {traders.map((t) => (
                                        <tr key={t.trader_ref} className="border-b border-border/50">
                                            <td className="py-1.5 pr-3 mono">{t.trader_ref}</td>
                                            <td className="pr-3">{t.billed ? "Yes" : "No"}</td>
                                            <td className="pr-3">{t.first_event_type || "—"}</td>
                                            <td className="pr-3">
                                                <span className={t.api_requests_this_cycle >= 950 ? "text-amber-500 font-semibold" : ""}>{t.api_requests_this_cycle}</span> / {t.fair_use_cap}
                                            </td>
                                            <td className="pr-3">{usd(t.overage_cost_usd)}</td>
                                            <td>
                                                {t.status === "overage" && <Badge className="bg-amber-500/15 text-amber-500 border-amber-500/30">+$0.005/req</Badge>}
                                                {t.status === "normal" && <Badge variant="outline">Normal</Badge>}
                                                {t.status === "not_activated" && <Badge variant="secondary">Not activated</Badge>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {/* Fair-Use Overlap Tracker (platform-wide) */}
            <div className="border border-border bg-card rounded p-4">
                <div className="overline mb-2 flex items-center gap-1.5"><Radio className="w-3.5 h-3.5" /> Fair-Use Overlap Tracker</div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="mau-overlap-table">
                        <thead><tr className="text-left text-muted-foreground border-b border-border">
                            <th className="py-1.5 pr-3">Client</th><th className="pr-3">Billed MAUs</th>
                            <th className="pr-3">Requests</th><th className="pr-3">Req/MAU</th><th>Flag</th>
                        </tr></thead>
                        <tbody>
                            {overlap.length === 0 && <tr><td colSpan={5} className="py-3 text-center text-muted-foreground">No clients yet.</td></tr>}
                            {overlap.map((o) => (
                                <tr key={o.client_id} className="border-b border-border/50">
                                    <td className="py-1.5 pr-3">{o.label}</td>
                                    <td className="pr-3">{o.total_billed_mau_this_cycle}</td>
                                    <td className="pr-3">{o.total_api_requests_this_cycle}</td>
                                    <td className="pr-3">{o.requests_per_mau}</td>
                                    <td>{o.flagged ? <Badge className="bg-red-500/15 text-red-500 border-red-500/30">Unoptimized loop?</Badge> : <Badge variant="outline">OK</Badge>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Feed cost vs margin + velocity pie */}
            <div className="grid md:grid-cols-2 gap-4">
                <div className="border border-border bg-card rounded p-4">
                    <div className="overline mb-2">Feed Cost vs Margin (platform-wide)</div>
                    {platform && (
                        <div className="space-y-2 text-sm">
                            <Row k="Total MAU revenue" v={usd(platform.total_revenue_usd)} />
                            <Row k="Live feed cost (est.)" v={usd(platform.total_feed_cost_usd)} />
                            <Row k="Gross margin" v={`${usd(platform.gross_margin_usd)} (${platform.gross_margin_pct ?? "—"}%)`} bold />
                        </div>
                    )}
                </div>
                <div className="border border-border bg-card rounded p-4">
                    <div className="overline mb-2">Velocity — client base by tier</div>
                    <div className="h-[160px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} label>
                                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                </Pie>
                                <RTooltip /><Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-2">
                        <div className="text-[11px] uppercase text-muted-foreground mb-1">Trigger Monitor (&gt;50 req/sec)</div>
                        {triggers.length === 0 ? (
                            <div className="text-xs text-muted-foreground">No clients over threshold right now.</div>
                        ) : triggers.map((t) => (
                            <div key={t.client_id} className="text-xs flex justify-between border-b border-border/50 py-1" data-testid="mau-trigger-flag-row">
                                <span>{t.label}</span><span className="text-red-500 font-semibold">{t.requests_per_second} req/s</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Feature flags */}
            <div className="border border-border bg-card rounded p-4">
                <div className="overline mb-3">Feature Flag & Nudge Toggles</div>
                <div className="grid md:grid-cols-2 gap-4">
                    {["obondo", "kimani"].map((persona) => (
                        <div key={persona}>
                            <div className="text-xs font-bold uppercase mb-2 text-muted-foreground">{persona}</div>
                            <div className="space-y-2">
                                {flags.filter((f) => f.persona === persona).map((f) => (
                                    <div key={f.key} className="flex items-center justify-between border border-border rounded px-3 py-2">
                                        <div>
                                            <div className="text-sm font-medium">{f.label}</div>
                                            <div className="text-[11px] text-muted-foreground">{f.description}</div>
                                        </div>
                                        <Switch checked={f.enabled} onCheckedChange={(v) => toggleFlag(f.key, v)} data-testid={`mau-flag-toggle-${f.key}`} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function StatBox({ label, value, sub, testId }) {
    return (
        <div className="border border-border bg-card rounded p-3" data-testid={testId}>
            <div className="text-[11px] uppercase text-muted-foreground">{label}</div>
            <div className="text-xl font-black mt-0.5">{value}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
        </div>
    );
}

function KillSwitchRow({ icon: Icon, label, active, onToggle, testId }) {
    return (
        <div className="flex items-center justify-between border border-border rounded px-3 py-2">
            <div className="flex items-center gap-2 text-sm"><Icon className="w-4 h-4 text-muted-foreground" /> {label}</div>
            <div className="flex items-center gap-2">
                <Badge variant={active ? "destructive" : "outline"}>{active ? "ON" : "OFF"}</Badge>
                <Switch checked={active} onCheckedChange={onToggle} data-testid={testId} />
            </div>
        </div>
    );
}

function Row({ k, v, bold }) {
    return (
        <div className="flex justify-between border-b border-border/50 py-1">
            <span className="text-muted-foreground">{k}</span>
            <span className={bold ? "font-black" : "font-semibold"}>{v}</span>
        </div>
    );
}
